import { supabase } from './supabase';
import { Database } from '../types/supabase';
import { travelCalendarService } from './calendar/travelCalendarService';

// Type definitions
type Trip = Database['public']['Tables']['trips']['Row'];
type TripInsert = Database['public']['Tables']['trips']['Insert'];
type TripUpdate = Database['public']['Tables']['trips']['Update'];

type Accommodation = Database['public']['Tables']['accommodations']['Row'];
type AccommodationInsert = Database['public']['Tables']['accommodations']['Insert'];
type AccommodationUpdate = Database['public']['Tables']['accommodations']['Update'];

type Transportation = Database['public']['Tables']['transportation']['Row'];
type TransportationInsert = Database['public']['Tables']['transportation']['Insert'];
type TransportationUpdate = Database['public']['Tables']['transportation']['Update'];

type TravelDocument = Database['public']['Tables']['travel_documents']['Row'];
type TravelDocumentInsert = Database['public']['Tables']['travel_documents']['Insert'];
type TravelDocumentUpdate = Database['public']['Tables']['travel_documents']['Update'];

type TravelBooking = Database['public']['Tables']['travel_bookings']['Row'];
type TravelBookingInsert = Database['public']['Tables']['travel_bookings']['Insert'];
type TravelBookingUpdate = Database['public']['Tables']['travel_bookings']['Update'];

export interface TravelTrip {
  id?: string;
  user_id?: string;
  title: string;
  description?: string;
  destination?: string;
  start_date?: string;
  end_date?: string;
  status?: 'planned' | 'in_progress' | 'completed' | 'cancelled';
  budget?: number;
  currency?: string;
  image_url?: string;
  created_at?: string;
  updated_at?: string;
}

export interface TravelAccommodation {
  id?: string;
  trip_id?: string;
  name: string;
  type?: 'hotel' | 'apartment' | 'house' | 'hostel' | 'resort' | 'other';
  address?: string;
  check_in_date?: string;
  check_out_date?: string;
  confirmation_number?: string;
  total_cost?: number;
  currency?: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface TravelTransportation {
  id?: string;
  trip_id?: string;
  type: 'flight' | 'train' | 'bus' | 'car' | 'ferry' | 'other';
  provider?: string;
  departure_location?: string;
  arrival_location?: string;
  departure_time?: string;
  arrival_time?: string;
  confirmation_number?: string;
  total_cost?: number;
  currency?: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface TravelDocument {
  id?: string;
  trip_id?: string;
  user_id?: string;
  name: string;
  type?: 'passport' | 'visa' | 'insurance' | 'vaccination' | 'covid_test' | 'other';
  document_number?: string;
  issue_date?: string;
  expiry_date?: string;
  issuing_country?: string;
  file_path?: string;
  file_name?: string;
  file_size?: number;
  status?: 'valid' | 'expired' | 'pending' | 'renewal_needed';
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface TravelTicket {
  id?: string;
  trip_id?: string;
  user_id?: string;
  type: 'attraction' | 'event' | 'museum' | 'tour' | 'activity' | 'other';
  name: string;
  provider?: string;
  location?: string;
  date?: string;
  confirmation_number?: string;
  booking_reference?: string;
  cost?: number;
  status?: 'planned' | 'booked' | 'confirmed' | 'cancelled' | 'used';
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface TravelItinerary {
  id?: string;
  trip_id?: string;
  user_id?: string;
  day_number?: number;
  date?: string;
  title?: string;
  description?: string;
  location?: string;
  start_time?: string;
  end_time?: string;
  activity_type?: 'sightseeing' | 'dining' | 'shopping' | 'relaxation' | 'transport' | 'other';
  cost?: number;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface TravelPassport {
  id?: string;
  user_id?: string;
  passport_number: string;
  surname: string;
  given_names: string;
  nationality: string;
  date_of_birth: string;
  place_of_birth?: string;
  date_of_issue: string;
  date_of_expiry: string;
  issuing_authority?: string;
  issuing_country?: string;
  file_path?: string;
  file_name?: string;
  file_size?: number;
  status?: 'valid' | 'expired' | 'lost' | 'stolen' | 'renewal_needed';
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface TravelBooking {
  id?: string;
  user_id?: string;
  trip_id?: string;
  type?: 'flight' | 'hotel' | 'car' | 'activity' | 'transport' | 'restaurant' | 'tour' | 'event';
  provider?: string;
  booking_reference?: string;
  confirmation_number?: string;
  start_date?: string;
  end_date?: string;
  departure?: string;
  arrival?: string;
  departure_time?: string;
  arrival_time?: string;
  location?: string;
  passengers?: string[];
  price?: number;
  currency?: string;
  status?: 'confirmed' | 'pending' | 'cancelled' | 'completed';
  notes?: string;
  email_confirmation?: string;
  created_at?: string;
  updated_at?: string;
}

export interface TravelStatistics {
  total_trips: number;
  planned_trips: number;
  active_trips: number;
  completed_trips: number;
  total_accommodations: number;
  total_transportation: number;
  total_documents: number;
  total_tickets: number;
  total_itineraries: number;
  total_passports: number;
}

// Travel Trips Service
export const travelTripsService = {
  async getAll(): Promise<TravelTrip[]> {
    const { data, error } = await supabase
      .from('trips')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<TravelTrip | null> {
    const { data, error } = await supabase
      .from('trips')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  async create(trip: TravelTrip): Promise<TravelTrip> {
    const { data, error } = await supabase
      .from('trips')
      .insert(trip)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, trip: Partial<TravelTrip>): Promise<TravelTrip> {
    const { data, error } = await supabase
      .from('trips')
      .update(trip)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('trips')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};

// Travel Accommodations Service
export const travelAccommodationsService = {
  async getAll(tripId?: string): Promise<TravelAccommodation[]> {
    let query = supabase
      .from('accommodations')
      .select('*')
      .order('created_at', { ascending: false });

    if (tripId) {
      query = query.eq('trip_id', tripId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<TravelAccommodation | null> {
    const { data, error } = await supabase
      .from('accommodations')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  async create(accommodation: TravelAccommodation): Promise<TravelAccommodation> {
    const { data, error } = await supabase
      .from('accommodations')
      .insert(accommodation)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, accommodation: Partial<TravelAccommodation>): Promise<TravelAccommodation> {
    const { data, error } = await supabase
      .from('accommodations')
      .update(accommodation)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('accommodations')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};

// Travel Transportation Service
export const travelTransportationService = {
  async getAll(tripId?: string): Promise<TravelTransportation[]> {
    let query = supabase
      .from('transportation')
      .select('*')
      .order('created_at', { ascending: false });

    if (tripId) {
      query = query.eq('trip_id', tripId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<TravelTransportation | null> {
    const { data, error } = await supabase
      .from('transportation')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  async create(transportation: TravelTransportation): Promise<TravelTransportation> {
    const { data, error } = await supabase
      .from('transportation')
      .insert(transportation)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, transportation: Partial<TravelTransportation>): Promise<TravelTransportation> {
    const { data, error } = await supabase
      .from('transportation')
      .update(transportation)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('transportation')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};

// Travel Documents Service
export const travelDocumentsService = {
  async getAll(tripId?: string): Promise<TravelDocument[]> {
    let query = supabase
      .from('travel_documents')
      .select('*')
      .order('created_at', { ascending: false });

    if (tripId) {
      query = query.eq('trip_id', tripId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<TravelDocument | null> {
    const { data, error } = await supabase
      .from('travel_documents')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  async create(document: TravelDocument): Promise<TravelDocument> {
    const { data, error } = await supabase
      .from('travel_documents')
      .insert(document)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, document: Partial<TravelDocument>): Promise<TravelDocument> {
    const { data, error } = await supabase
      .from('travel_documents')
      .update(document)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('travel_documents')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async uploadFile(file: File, userId: string): Promise<{ path: string; url: string }> {
    const fileName = `${userId}/${Date.now()}_${file.name}`;
    const { data, error } = await supabase.storage
      .from('travel-documents')
      .upload(fileName, file);

    if (error) throw error;

    const { data: urlData } = supabase.storage
      .from('travel-documents')
      .getPublicUrl(fileName);

    return {
      path: fileName,
      url: urlData.publicUrl
    };
  },

  async deleteFile(filePath: string): Promise<void> {
    const { error } = await supabase.storage
      .from('travel-documents')
      .remove([filePath]);

    if (error) throw error;
  }
};

// Travel Tickets Service
export const travelTicketsService = {
  async getAll(tripId?: string): Promise<TravelTicket[]> {
    let query = supabase
      .from('travel_tickets')
      .select('*')
      .order('created_at', { ascending: false });

    if (tripId) {
      query = query.eq('trip_id', tripId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<TravelTicket | null> {
    const { data, error } = await supabase
      .from('travel_tickets')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  async create(ticket: TravelTicket): Promise<TravelTicket> {
    const { data, error } = await supabase
      .from('travel_tickets')
      .insert(ticket)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, ticket: Partial<TravelTicket>): Promise<TravelTicket> {
    const { data, error } = await supabase
      .from('travel_tickets')
      .update(ticket)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('travel_tickets')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};

// Travel Itineraries Service
export const travelItinerariesService = {
  async getAll(tripId?: string): Promise<TravelItinerary[]> {
    let query = supabase
      .from('itineraries')
      .select('*')
      .order('date', { ascending: true })
      .order('start_time', { ascending: true });

    if (tripId) {
      query = query.eq('trip_id', tripId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<TravelItinerary | null> {
    const { data, error } = await supabase
      .from('itineraries')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  async create(itinerary: TravelItinerary): Promise<TravelItinerary> {
    const { data, error } = await supabase
      .from('itineraries')
      .insert(itinerary)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, itinerary: Partial<TravelItinerary>): Promise<TravelItinerary> {
    const { data, error } = await supabase
      .from('itineraries')
      .update(itinerary)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('itineraries')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};

// Travel Bookings Service
export const travelBookingsService = {
  async getAll(tripId?: string): Promise<TravelBooking[]> {
    let query = supabase
      .from('travel_bookings')
      .select('*')
      .order('created_at', { ascending: false });

    if (tripId) {
      query = query.eq('trip_id', tripId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<TravelBooking | null> {
    const { data, error } = await supabase
      .from('travel_bookings')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  async create(booking: TravelBooking): Promise<TravelBooking> {
    const { data, error } = await supabase
      .from('travel_bookings')
      .insert(booking)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, booking: Partial<TravelBooking>): Promise<TravelBooking> {
    const { data, error } = await supabase
      .from('travel_bookings')
      .update(booking)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('travel_bookings')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};

// Travel Passports Service
export const travelPassportsService = {
  async getAll(): Promise<TravelPassport[]> {
    const { data, error } = await supabase
      .from('travel_passports')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<TravelPassport | null> {
    const { data, error } = await supabase
      .from('travel_passports')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  async create(passport: TravelPassport): Promise<TravelPassport> {
    const { data, error } = await supabase
      .from('travel_passports')
      .insert(passport)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, passport: Partial<TravelPassport>): Promise<TravelPassport> {
    const { data, error } = await supabase
      .from('travel_passports')
      .update(passport)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('travel_passports')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async uploadFile(file: File, userId: string): Promise<{ path: string; url: string }> {
    const fileName = `${userId}/${Date.now()}_${file.name}`;
    const { data, error } = await supabase.storage
      .from('travel-documents')
      .upload(fileName, file);

    if (error) throw error;

    const { data: urlData } = supabase.storage
      .from('travel-documents')
      .getPublicUrl(fileName);

    return {
      path: fileName,
      url: urlData.publicUrl
    };
  },

  async deleteFile(filePath: string): Promise<void> {
    const { error } = await supabase.storage
      .from('travel-documents')
      .remove([filePath]);

    if (error) throw error;
  }
};

// Travel Statistics Service
export const travelStatisticsService = {
  async getStatistics(): Promise<TravelStatistics> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    try {
      // Get counts from each table
      const [tripsData, accommodationsData, transportationData, documentsData, bookingsData] = await Promise.all([
        supabase.from('trips').select('id, status', { count: 'exact' }).eq('user_id', user.id),
        supabase.from('accommodations').select('id', { count: 'exact' }),
        supabase.from('transportation').select('id', { count: 'exact' }),
        supabase.from('travel_documents').select('id', { count: 'exact' }).eq('user_id', user.id),
        supabase.from('travel_bookings').select('id', { count: 'exact' }).eq('user_id', user.id)
      ]);

      const totalTrips = tripsData.count || 0;
      const plannedTrips = tripsData.data?.filter(t => t.status === 'planned').length || 0;
      const activeTrips = tripsData.data?.filter(t => t.status === 'in_progress').length || 0;
      const completedTrips = tripsData.data?.filter(t => t.status === 'completed').length || 0;

      return {
        total_trips: totalTrips,
        planned_trips: plannedTrips,
        active_trips: activeTrips,
        completed_trips: completedTrips,
        total_accommodations: accommodationsData.count || 0,
        total_transportation: transportationData.count || 0,
        total_documents: documentsData.count || 0,
        total_tickets: bookingsData.count || 0,
        total_itineraries: 0, // Not implemented yet
        total_passports: 0 // Not implemented yet
      };
    } catch (error) {
      console.error('Error getting travel statistics:', error);
      return {
        total_trips: 0,
        planned_trips: 0,
        active_trips: 0,
        completed_trips: 0,
        total_accommodations: 0,
        total_transportation: 0,
        total_documents: 0,
        total_tickets: 0,
        total_itineraries: 0,
        total_passports: 0
      };
    }
  }
};

// Export all services
export const travelService = {
  trips: travelTripsService,
  accommodations: travelAccommodationsService,
  transportation: travelTransportationService,
  documents: travelDocumentsService,
  tickets: travelTicketsService,
  itineraries: travelItinerariesService,
  bookings: travelBookingsService,
  passports: travelPassportsService,
  statistics: travelStatisticsService,

  // Calendar integration methods
  async createTripWithCalendar(trip: TravelTrip): Promise<TravelTrip> {
    try {
      // Create the trip
      const createdTrip = await this.trips.create(trip);
      
      // Create calendar events for the trip
      await travelCalendarService.createTripCalendarEvents(createdTrip.id!);
      
      return createdTrip;
    } catch (error) {
      console.error('Error creating trip with calendar:', error);
      throw error;
    }
  },

  async updateTripWithCalendar(id: string, trip: Partial<TravelTrip>): Promise<TravelTrip> {
    try {
      // Update the trip
      const updatedTrip = await this.trips.update(id, trip);
      
      // Update calendar events for the trip
      await travelCalendarService.updateTripCalendarEvents(id);
      
      return updatedTrip;
    } catch (error) {
      console.error('Error updating trip with calendar:', error);
      throw error;
    }
  },

  async deleteTripWithCalendar(id: string): Promise<void> {
    try {
      // Delete calendar events first
      await travelCalendarService.deleteTripCalendarEvents(id);
      
      // Then delete the trip
      await this.trips.delete(id);
    } catch (error) {
      console.error('Error deleting trip with calendar:', error);
      throw error;
    }
  },

  async createAccommodationWithCalendar(accommodation: TravelAccommodation): Promise<TravelAccommodation> {
    try {
      // Create the accommodation
      const createdAccommodation = await this.accommodations.create(accommodation);
      
      // Create calendar events for the accommodation
      await travelCalendarService.createAccommodationCalendarEvents(createdAccommodation.id!);
      
      return createdAccommodation;
    } catch (error) {
      console.error('Error creating accommodation with calendar:', error);
      throw error;
    }
  },

  async createTransportationWithCalendar(transportation: TravelTransportation): Promise<TravelTransportation> {
    try {
      // Create the transportation
      const createdTransportation = await this.transportation.create(transportation);
      
      // Create calendar events for the transportation
      await travelCalendarService.createTransportationCalendarEvents(createdTransportation.id!);
      
      return createdTransportation;
    } catch (error) {
      console.error('Error creating transportation with calendar:', error);
      throw error;
    }
  },

  async syncAllTravelWithCalendar(): Promise<void> {
    try {
      await travelCalendarService.syncAllTravelWithCalendar();
    } catch (error) {
      console.error('Error syncing travel with calendar:', error);
      throw error;
    }
  },

  async getUpcomingTravelEvents(days: number = 30): Promise<any[]> {
    try {
      return await travelCalendarService.getUpcomingTravelEvents(days);
    } catch (error) {
      console.error('Error getting upcoming travel events:', error);
      throw error;
    }
  },

  async getTripCalendarEvents(tripId: string): Promise<any[]> {
    try {
      return await travelCalendarService.getTripCalendarEvents(tripId);
    } catch (error) {
      console.error('Error getting trip calendar events:', error);
      throw error;
    }
  },

  // Enhanced statistics with calendar data
  async getEnhancedStatistics(): Promise<TravelStatistics & { upcomingEvents: number; calendarSynced: boolean }> {
    try {
      const basicStats = await this.statistics.getStatistics();
      const upcomingEvents = await this.getUpcomingTravelEvents(30);
      
      return {
        ...basicStats,
        upcomingEvents: upcomingEvents.length,
        calendarSynced: true
      };
    } catch (error) {
      console.error('Error getting enhanced statistics:', error);
      throw error;
    }
  }
}; 